'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'

/*
Note: This has been updated 2026-04-06 to behave closely to the IBM's established Carbon Design system.
https://carbondesignsystem.com/components/pagination/usage/#live-demo 
*/

export type PaginationSpreadProps = {
  numResults: number
  startingNumber: number
  endingNumber: number
}

export const PaginationSpread = ({
  numResults,
  startingNumber,
  endingNumber,
}: PaginationSpreadProps) => {
  return (
    <span className="font-body text-base font-normal outline-foreground">
      {numResults
        ? startingNumber === endingNumber
          ? `${startingNumber} of ${numResults}`
          : `${startingNumber}-${endingNumber} of ${numResults}`
        : 'No results'}
    </span>
  )
}
export type PaginationProps = {
  numResults: number
  resultsPerPage: number
  currentPage: number
  onPageChange: (value: number) => void
  className?: string
}

export const Pagination = ({
  numResults,
  resultsPerPage,
  currentPage,
  onPageChange,
  className,
}: PaginationProps) => {
  const maxPage = Math.ceil(numResults / resultsPerPage)
  const canGoBack = currentPage > 1
  const canGoForward = currentPage < maxPage
  const remaining = canGoForward ? resultsPerPage : numResults % resultsPerPage || resultsPerPage
  const startingNumber = (currentPage - 1) * resultsPerPage + 1
  const endingNumber = startingNumber + remaining - 1

  const goBack = () => {
    if (!canGoBack) return

    onPageChange(Math.max(currentPage - 1, 1))
  }

  const goForward = () => {
    if (!canGoForward) return

    onPageChange(Math.min(currentPage + 1, maxPage))
  }

  const goToPage = (chosenPage: string) => {
    const cleanPage = Number.parseInt(chosenPage)

    if (isNaN(cleanPage) || cleanPage > maxPage || cleanPage < 1 || cleanPage === currentPage)
      return

    onPageChange(cleanPage)
  }

  const disabled = numResults <= resultsPerPage

  return (
    <nav
      aria-label="Pagination"
      role="navigation"
      className={cn('flex w-full flex-row items-center gap-3', className)}
    >
      <div className="flex flex-row gap-2">
        <button
          onClick={goBack}
          aria-label="Previous page"
          className={cn(
            buttonVariants({
              size: 'default',
              variant: 'icon',
            }),
            'gap-1 px-3',
          )}
          disabled={!canGoBack}
        >
          <ChevronLeft className="size-5" />
        </button>
        <span className="sr-only">{`Page ${currentPage} of ${Math.max(maxPage, 1)}`}</span>
        <Select value={currentPage.toString()} onValueChange={goToPage} disabled={disabled}>
          <SelectTrigger
            className={cn(
              'border-none bg-primary text-primary-foreground hover:border-none hover:bg-primary/90',
            )}
            aria-label="Select page"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: Math.max(maxPage, 1) }, (_, i) => i + 1).map((i) => (
              <SelectItem key={i} value={i.toString()}>
                {i}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          onClick={goForward}
          aria-label="Next page"
          className={cn(
            buttonVariants({
              size: 'default',
              variant: 'icon',
            }),
            'gap-1 px-3',
          )}
          disabled={!canGoForward}
        >
          <ChevronRight className="size-5" />
        </button>
      </div>
      <PaginationSpread
        numResults={numResults}
        startingNumber={startingNumber}
        endingNumber={endingNumber}
      />
    </nav>
  )
}
